﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { FETCH_BUSINESS_CENTER_SUCCESS, FINAL_BUSINESS_CENTER_REQUEST, INITIAL_BUSINESS_CENTER_REQUEST } from "@/redux/types/maintainers/business_center.type";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import classes from "../../maintainers.module.scss";

import api from "@/services/api.service";
import { getToday, hasPermission } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import Detail from "@/components/Detail/Detail";

import BusinessCenterTableHead from "./BusinessCenterTableHead";
import BusinessCenterTableBody from "./BusinessCenterTableBody";
import BusinessCenterForm from "./BusinessCenterForm/BusinessCenterForm";
import BusinessCenterFilter from "./BusinessCenterFilter";
import BusinessCenterParentForm from "./BusinessCenterForm/BusinessCenterParentForm";

import { filters } from "./business_center.constants";

const BusinessCenter = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers, estates, categories, species, varieties, quarters, crops } = useSelector(
		(state) => state.maintainers_business_center,
	);

	const { customer } = useSelector((state) => state.auth.user);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({ ...filters });
	const [showFilter, setShowFilter] = useState(false);
	const [permissions, setPermissions] = useState([]);
	const [estates_parents, setEstatesParents] = useState([]);
	const [itemType, setItemType] = useState("");
	const [showDelete, setShowDelete] = useState(false);

	const [titleDetail, setTitleDetail] = useState("");
	const [itemsDetail, setItemsDetail] = useState([]);
	const [showDetail, setShowDetail] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_BUSINESS_CENTER_REQUEST });
		api.get("/admin/business_center", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			...filter,
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_id, order, orderType, paginate, page, filter]);

	const updateData = (response) => {
		const {
			business_center,
			customers,
			estates_parents: estatesParents,
			estates,
			categories,
			species,
			varieties,
			quarters,
			crops,
			permissions: allPermissions,
		} = response;

		dispatch({
			type: FETCH_BUSINESS_CENTER_SUCCESS,
			payload: {
				business_center: business_center.data,
				last_page: business_center.last_page,
				from: business_center.from,
				to: business_center.to,
				quantity: business_center.total,
				customers: customers,
				estates: estates,
				species: species,
				categories: categories,
				varieties: varieties,
				quarters: quarters,
				crops: crops,
			},
		});
		setPermissions(allPermissions);
		setEstatesParents(estatesParents);
		setLoaded(true);
	};

	const handleDelete = (item, type) => {
		setItemType(type);
		setSelected(item.id);
		setShowDelete(true);
	};

	const handleArchive = (item) => {
		dispatch({ type: INITIAL_BUSINESS_CENTER_REQUEST });
		setPage(1);

		api.post("/admin/business_center/archive", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			...filter,
			item: item,
		}).then((response) => {
			updateData(response);
		});
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_BUSINESS_CENTER_REQUEST });

		let paramsDelete = {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			itemType,
		};

		api.post("/admin/business_center/delete", paramsDelete).then((response) => {
			setSelected("");

			if (Object.prototype.hasOwnProperty.call(response, "business_center")) {
				toast.success("Centro de Costo eliminado correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_BUSINESS_CENTER_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/business_center/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			...filter,
		}).then((response) => {
			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	const confirmSaveParent = (form, modalFetching, afterSave) => {
		form.businessCenterSaveParent = Boolean(true);

		modalFetching(true);
		setPage(1);

		let newBusinessCenters = form.businessCenters.map((item) => {
			return { ...item, estates: form.estates, estate_parent_id: form.estate_parent_id };
		});
		let newForm = { ...form, businessCenters: newBusinessCenters };

		api.post("/admin/business_center/save", {
			...newForm,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			...filter,
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "business_center")) {
				updateData(response);
				modalFetching(false);
				afterSave();
			} else {
				modalFetching(false);
			}
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.filterType !== filter.filter_type) {
			new_filter = { ...new_filter, filter_type: params.filterType };
		}
		if (params.estateFilter !== filter.filter_estate) {
			new_filter = { ...new_filter, filter_estate: params.estateFilter };
		}
		if (params.categoryFilter !== filter.filter_category) {
			new_filter = {
				...new_filter,
				filter_category: params.categoryFilter,
			};
		}
		if (params.specieFilter !== filter.filter_specie) {
			new_filter = { ...new_filter, filter_specie: params.specieFilter };
		}
		if (params.varietyFilter !== filter.filter_variety) {
			new_filter = {
				...new_filter,
				filter_variety: params.varietyFilter,
			};
		}
		if (params.quarterFilter !== filter.filter_quarter) {
			new_filter = {
				...new_filter,
				filter_quarter: params.quarterFilter,
			};
		}
		setFilter(new_filter);
		setShowFilter(false);
	};

	const handleShow = (title, items) => {
		setTitleDetail(title);
		setItemsDetail(items);
		setShowDetail(true);
	};

	const exportExcel = () => {
		const filename = `Centros de Costo ${getToday()}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {
						page,
						paginate,
						order,
						orderType,
						search,
						estate_id,
						type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
						...filter,
					},
					show: true,
					url: "/admin/business_center/export_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Centros de Costo"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Centro de Costo"}
				action={() =>
					navigate({
						pathname: customer.use_sectors ? "/configuracion/centro_costo/crear_grupo" : "/configuracion/centro_costo/crear",
						state: "/configuracion/centro_costo",
					})
				}
				style={{ width: "200px" }}
				back_url={"/configuracion?section=businessCenter"}
			/>

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					{showFilter && <BusinessCenterFilter filter={filter} applyFilter={applyFilter} />}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />

							<Col md={9}></Col>

							<Col md={2} className={classes.colOptionButton}>
								<div className={`${classes.optionButton} ${classes.exportInventory}`} onClick={() => exportExcel()}>
									<span>Excel</span>
								</div>
							</Col>
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<BusinessCenterTableHead
										order={order}
										setOrder={setOrder}
										orderType={orderType}
										setOrderType={setOrderType}
										useSectors={customer.use_sectors}
									/>
									<BusinessCenterTableBody
										filter={filter}
										handleDelete={handleDelete}
										handleArchive={handleArchive}
										permissions={permissions}
										useSectors={customer.use_sectors}
										handleShow={handleShow}
									/>
								</Table>
							</div>
						</Row>
						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<Detail title={titleDetail} items={itemsDetail} isOpen={showDetail} handleClose={setShowDetail} />

					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} isOverlay={true} />

					<BusinessCenterParentForm
						handleDelete={handleDelete}
						confirmSaveParent={confirmSaveParent}
						estates_parents={estates_parents}
						estates={estates}
						categories={categories}
						species={species}
						varieties={varieties}
						quarters={quarters}
						crops={crops}
					/>

					<BusinessCenterForm
						customers={customers}
						estates_parents={estates_parents}
						estates={estates}
						categories={categories}
						species={species}
						varieties={varieties}
						quarters={quarters}
						crops={crops}
						confirmSave={confirmSave}
						permissions={permissions}
					/>
				</>
			)}
		</>
	);
};

export default BusinessCenter;
